import clsx from "clsx";

import { ContentTitle } from "./ContentTitle";

import { MobileAppointmentSummary } from "~/components/Appointment/MobileAppointmentSummary";
import { useStepLinkState } from "~/hooks/useStepLinkState";
import { GoBackButton } from "~/modules/go-back-api";
import { Step } from "~/modules/StateManager";
import { useStateManager } from "~/modules/StateManager.hooks";
import { SummaryProps } from "~/routes/$enterprise+/plugin+/$sessionId.schedule";
import type { StepConfig } from "~/utils/getFlow";

export function MobileSummaryContainer({
  hideLockedSteps = false,
  info,
}: Omit<SummaryProps, "children">) {
  const stateManager = useStateManager();
  const { location, timetable } = stateManager.state;
  const { employee, meetingType, office, subject } = info;

  const visibleSteps = stateManager.visibleSteps.filter((step) =>
    hideLockedSteps ? !step.locked : true,
  );

  return (
    <div data-component="SummaryContainerMobile">
      <MobileAppointmentSummary
        employee={employee}
        location={{
          geolocation: location?.geolocation ?? null,
          address: location?.formattedAddress ?? null,
        }}
        meetingType={meetingType}
        office={office}
        possibility={
          timetable.start && timetable.end ? { start: timetable.start, end: timetable.end } : null
        }
        subject={subject}
        isMobileCollapsible
      />

      <div className="px-3xs pb-xs pt-md">
        <ContentTitle isMobile />

        <div className="mt-sm flex items-baseline gap-3xs">
          {visibleSteps.map((step) => (
            <div className="flex-1" key={step.id}>
              <StepProgress step={step} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepProgress({ step }: { step: Step }) {
  const { isActive, isEditable } = useStepLinkState({ step });

  const highlight = step.hasValue || isActive;

  return (
    <StepLink
      data-component="StepLink"
      highlight={highlight}
      isEditable={isEditable}
      stepId={step.id}
    >
      <div
        className={clsx("h-[6px] flex-1", highlight ? "bg-brand-secondary" : "bg-gray-200")}
        data-active={isActive}
        data-component="StepIndicatorMobile"
      />
    </StepLink>
  );
}

interface StepLinkProps extends React.PropsWithChildren {
  isEditable?: boolean;
  stepId: StepConfig["id"];
  highlight?: boolean;
}

function StepLink({ children, highlight, isEditable, stepId }: StepLinkProps) {
  if (!isEditable) {
    return <>{children}</>;
  }

  return (
    <GoBackButton
      className={clsx(
        "border-none",
        "h-[6px] w-full text-brand",
        "h-[6px] hover:underline",
        highlight ? "bg-brand-secondary" : "bg-gray-200",
      )}
      formClassName="flex-1"
      stepId={stepId}
    >
      {children}
    </GoBackButton>
  );
}
